import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEstateOptions } from '@/api/estate-options';
import { cn } from '@/lib/utils';

import { StatusType } from '../constants/status-type';
import {
  type EstateAvailability,
  getEstateAvailabilityLabel,
} from '../constants/estate-availability';
import { EstateAvailabilityBadge } from './estate-availability-badge';

const CHIP_CLASS_NAME =
  'whitespace-nowrap rounded-full border border-border bg-muted py-0.5 text-xs font-medium text-foreground';

/** The fields the chips read, so both /estates list rows and project estates fit. */
export type EstateChipsData = {
  status?: string | null;
  availability?: EstateAvailability;
  estateDetails?: { propertyType?: string | null; marketType?: string | null } | null;
};

/** Resolved property type and market type titles of an estate row. */
export const useEstateChipLabels = (estate: EstateChipsData) => {
  const { data: estateOptions } = useEstateOptions();
  const rawPropertyType = estate.estateDetails?.propertyType ?? undefined;
  const rawMarketType = estate.estateDetails?.marketType ?? undefined;

  return useMemo(() => {
    let propertyType: string | undefined = rawPropertyType;
    let marketType: string | undefined = rawMarketType;

    if (propertyType && estateOptions?.propertyType) {
      propertyType =
        estateOptions.propertyType.find(({ id }) => id === propertyType)?.title ?? propertyType;
    }

    if (marketType && estateOptions?.marketingType) {
      marketType =
        estateOptions.marketingType.find(({ id }) => id === marketType)?.title ?? marketType;
    }

    return { propertyType, marketType };
  }, [rawPropertyType, rawMarketType, estateOptions]);
};

type EstateChipsProps = {
  estate: EstateChipsData;
  className?: string;
  /** Tighter horizontal padding of the chips */
  compact?: boolean;
  /**
   * 'badge' shows the coloured availability badge, only for estate rows (project rows of the
   * combined list have no availability). 'neutral' always shows availability as a grey chip,
   * an empty one reads "Not specified".
   */
  availabilityStyle?: 'badge' | 'neutral';
};

/** Property type, market type, status and availability chips in design style. */
export const EstateChips = ({
  estate,
  className,
  compact,
  availabilityStyle = 'badge',
}: EstateChipsProps) => {
  const { t } = useTranslation();
  const { propertyType, marketType } = useEstateChipLabels(estate);
  const chipClassName = cn(CHIP_CLASS_NAME, compact ? 'px-2' : 'px-2.5');

  return (
    <div className={className ?? 'flex flex-wrap items-center gap-1.5'}>
      {propertyType ? <span className={chipClassName}>{propertyType}</span> : null}
      {marketType ? <span className={chipClassName}>{marketType}</span> : null}
      {estate.status ? (
        <span className={chipClassName}>
          {estate.status === StatusType.draft ? t('draft') : t('public')}
        </span>
      ) : null}
      {availabilityStyle === 'neutral' ? (
        <span className={chipClassName}>
          {getEstateAvailabilityLabel(t, estate.availability ?? null)}
        </span>
      ) : 'estateDetails' in estate ? (
        <EstateAvailabilityBadge availability={estate.availability ?? null} />
      ) : null}
    </div>
  );
};
